<template>
  <!-- 26-6-9-popup.vue -->
  <s-dialog to="stds-dialog-UTC" size:sm :open="true">
    <s-dialog-overlay />
    <s-dialog-panel class="relative !w-[56rem] rounded-[2.8rem] overflow-visible">
      <st-dialog-header class="pt-20 px-32" @clickClose="handleClose">
        <span class="text-on-surface-elevation-1 !font-bold">{{ $t('studio.execution_setting.live_apply_popup_title') }}</span>
      </st-dialog-header>
      <s-dialog-content class="!px-32 py-16 m-0 overflow-visible min-h-[16.8rem]">
        <s-dialog-content-body class="flex flex-col gap-24 !py-0 overflow-visible">
          <div v-if="!isWebPlatform" class="flex flex-col gap-4">
            <st-form-title
              :formTitle="$t('studio.execution_setting.live_apply_popup_select_build')"
              required
            />
            <s-text as="p" role="cap1" class="flex flex-col gap-4 text-placeholder mb-8">
              <safe-html :html="$t('studio.execution_setting.live_apply_popup_select_build_guide')" />
            </s-text>
            <div class="flex-1 relative">
              <s-dropdown
                v-model="selectedValue"
                size="lg"
                variant="line"
                :distance="0"
                :offset="[0, 0]"
                :closeOnClick="true"
                placement="bottom-start"
                theme="limit-dropdown-height"
                class="w-full"
                :isDisabled="isDisabled"
                @update:modelValue="handleDropdownChange"
              >
                <s-dropdown-button
                  class="w-full"
                  :class="{ 'text-placeholder': !selectedValue }"
                >
                  {{ selectedValue ? (selectedValue.mode + ' ' + selectedValue.name) : $t('studio.prj_prod_mngmt.rating_build_review.self_review.rating_reg.grac_self_review.build_required_msg') }}
                </s-dropdown-button>
                <template #dropdownItems>
                  <st-dropdown-item-disable
                    v-for="item in valueList"
                    :key="`item-${item.id}`"
                    :item="item"
                    :isDisabled="isItemDisabled(item)"
                    @click="handleItemClick"
                  />
                </template>
              </s-dropdown>
            </div>
            <st-error-message
              v-if="buildSelectionError"
              :errorMessage="buildSelectionError"
            />
            <div
              v-if="hasComingSoonBuildWarning"
              class="text-brand-primary text-xs leading-xs font-regular mt-8 flex items-start"
            >
              <span>{{ comingSoonBuildWarning }}</span>
            </div>
          </div>
          <div v-if="shouldShowLiveDateSelection()" class="flex flex-col gap-8">
            <st-form-title
              :formTitle="$t('studio.execution_setting.live_apply_date_title')"
              required
            />
            <div class="flex flex-col gap-8">
              <radio-group
                v-model="liveDate"
                :options="optionsApplyLive"
                rules="required"
                class="flex flex-col gap-8"
                :optionProps="{
                  size: 'sm',
                  align: 'middle'
                }"
                @update:modelValue="handleRadioLiveChange"
              />
              <template v-if="liveDate === Confirmation.NO">
                <datetime-picker
                  name="dateTimeBuild"
                  showUtc
                  :modelValue="selectedDate"
                  @update:modelValue="handleDateTimeChange"
                />
                <st-error-message
                  v-if="dateTimeError"
                  :errorMessage="dateTimeError"
                />
              </template>
            </div>
          </div>
        </s-dialog-content-body>
      </s-dialog-content>
      <s-dialog-footer class="!pt-24">
        <SButton class="!w-264" @click="handleApply">{{ $t('studio.execution_setting.live_apply_popup_title') }}</SButton>
      </s-dialog-footer>
    </s-dialog-panel>
  </s-dialog>
  <SPortalTarget name="stds-dialog-UTC" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useForm, useSetFieldValue } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { createApplyLiveReleaseBuildApi } from '@/apis/build.api';
import { fetchInstallPathAndPcClientSettingsOnTabApi, fetchListBuildReleaseAfterProductLiveRunOptionsApi } from '@/apis/launch-settings.api';
import { getBuildMetaReleaseInfoApi } from '@/apis/release-plan.api';
import ConfirmReleaseBuild from '@/components/build/dialog/confirm-release-build.vue';
import SafeHtml from '@/components/common/safe-html.vue';
import StDialogHeader from '@/components/common/st-dialog-header.vue';
import StDropdownItemDisable from '@/components/common/st-dropdown-item-disable.vue';
import StErrorMessage from '@/components/common/st-error-message.vue';
import StFormTitle from '@/components/common/st-form-title.vue';
import DatetimePicker from '@/components/validation/datetime-picker.vue';
import RadioGroup from '@/components/validation/radio-group.vue';
import { showAlert, showConfirm, showDialog } from '@/composables/useDialog';
import { API_SUCCESS_STATUS_CODE } from '@/constants/api.const';
import { APPLY_LIVE_RELEASE_TYPE, RELEASE_STATUS_BUILD } from '@/constants/build.const';
import { PLATFORM } from '@/constants/common.const';
import { COMMON_ERROR_MESSAGE_KEY } from '@/constants/error.const';
import { RUN_OPTION_STATUS } from '@/constants/launch-setting.const';
import {
  PLAN_STATUS,
  PRODUCT_TYPE_DETAIL,
  VERIFY_STATUS
} from '@/constants/products.const';
import { PRODUCT_BUILD_PAGE_URL, PRODUCT_LAUNCH_SETTING_PAGE_URL } from '@/constants/url.const';
import { Confirmation } from '@/enums/common.enum';
import { RevisionType } from '@/enums/rating.enum';
import { RuleNames } from '@/enums/validation.enum';
import useProductStore from '@/stores/product.store';
import type { ApplyLiveReleaseBuildParams } from '@/types/build/build.type';
import type { BuildLaunchSettings } from '@/types/launch-settings/launch-settings.type';
import { redirectTo } from '@/utils/common.util';
import { generateErrorMsg } from '@/utils/validation.util';

// Define API response types
type ApiResponse = {
  contents?: any[];
  [key: string]: any;
};

// Define BUILD ITEM interface
type BuildItem = {
  id: string;
  buildNo: number;
  description?: string;
  releaseStatus: string;
  mode: string;
  name: string;
  gameId: string;
  revisionTypes?: string;
  releaseYn?: string;
};

const props = defineProps({
  onClose: {
    type: Function,
    default: () => {}
  },
  platformType: {
    type: String,
    default: ''
  },
  gameId: {
    type: String,
    default: ''
  },
  verifyStatus: {
    type: String,
    default: ''
  },
  planStatus: {
    type: String,
    default: ''
  }
});

const productStore = useProductStore();
const { currentProductDetailType } = storeToRefs(productStore);

const isWebPlatform = computed(() => {
  return props.platformType === PLATFORM.WEB;
});

const emit = defineEmits(['update:modelValue']);

const router = useRouter();
const { t } = useI18n();
const isDisabled = ref(false);

const selectedValue = ref<BuildItem | null>(null);
const valueList = ref<BuildItem[]>([]);

// DateTime picker related variables
const liveDate = ref<string>(Confirmation.YES);
const dateTimeError = ref<string>('');
const selectedDate = ref<Date | string>(new Date());

const buildSelectionError = ref<string>('');
const hasComingSoonBuildWarning = ref<boolean>(false);
const comingSoonBuildWarning = ref<string>(t('studio.execution_setting.select_scheduled_build.warning_msg'));

const defaultDateTime = computed(() => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
});

// Form handling
const { handleSubmit } = useForm({
  initialValues: {
    dateTimeBuild: defaultDateTime.value,
    live: { selectedProductsId: [] }
  }
});

const setDateTimeBuildValue = useSetFieldValue<Date | string>('dateTimeBuild');

const optionsApplyLive = computed(() => {
  return [
    {
      value: Confirmation.YES,
      label: t('studio.prj_prod.build_mgmt.build_detail.post_release_build_update.case_self_review.change_report_complete.immed_apple')
    },
    {
      value: Confirmation.NO,
      label: t('studio.prj_prod.build_mgmt.build_detail.post_release_build_update.case_self_review.change_report_complete.schedule_apply'),
      isDisabled: isScheduledOptionDisabled.value
    }
  ];
});

const isScheduledOptionDisabled = computed(() => {
  return selectedValue.value?.releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE;
});

// Check if there are any builds with COMING_SOON status
const checkForComingSoonBuilds = () => {
  return valueList.value.some((item: BuildItem) => {
    return item.releaseStatus === RELEASE_STATUS_BUILD.COMING_SOON;
  });
};

// Validate the datetime selection
const validateDateTime = (value: string | Date | undefined) => {
  if (!value) {
    dateTimeError.value = generateErrorMsg('studio.prj_prod.build_mgmt.build_detail.post_release_build_update.case_self_review.change_report_complete.schedule_req_msg1', RuleNames.REQUIRED);
  } else {
    const now = new Date(); // Current date and time
    const selectedDate = new Date(value);

    // Compare full timestamp
    if (selectedDate.getTime() <= now.getTime()) {
      dateTimeError.value = generateErrorMsg('studio.prj_prod.build_mgmt.build_detail.post_release_build_update.case_self_review.change_report_complete.schedule_invalid_msg2', RuleNames.CHECK_DUPLICATE);
    } else {
      dateTimeError.value = '';
    }
  }
};

const handleRadioLiveChange = (value: string) => {
  if (value === Confirmation.NO) {
    setDateTimeBuildValue(selectedDate.value);
    validateDateTime(selectedDate.value);
  }
};

const handleDateTimeChange = (value: string | Date | undefined) => {
  const newDate = parseDate(value);
  updateDateState(newDate);
};

// Parse date from various formats
const parseDate = (value: string | Date | undefined): Date => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? defaultDateTime.value : parsedDate;
  }
  return defaultDateTime.value;
};

// Update the date state
const updateDateState = (date: Date) => {
  selectedDate.value = date;
  setDateTimeBuildValue(date);
  validateDateTime(date);
};

const getReleaseStatusText = (item: BuildLaunchSettings): string => {
  if (currentProductDetailType.value === PRODUCT_TYPE_DETAIL.UTILITY_BASIC ||
    currentProductDetailType.value === PRODUCT_TYPE_DETAIL.UTILITY_DEMO ||
    currentProductDetailType.value === PRODUCT_TYPE_DETAIL.UTILITY_DLC) {
    if (item.releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE) {
      return t('studio.execution_setting.select_build.build_category_live');
    } else {
      return t('studio.execution_setting.select_build.build_category_utility');
    }
  }

  // Case direct_yn = Confirmation.YES
  if (item.directYn === Confirmation.YES) {
    if (item.releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE) {
      return t('studio.execution_setting.select_build.build_category_live');
    } else {
      return t('studio.execution_setting.select_build.build_category_direct_input');
    }
  }

  // Case direct_yn = 'N'
  if (item.releaseStatus === RELEASE_STATUS_BUILD.VERIFY_UNDER_REVIEW) {
    return t('studio.execution_setting.select_build.build_category_under_review');
  } else if (item.releaseStatus === RELEASE_STATUS_BUILD.VERIFY_REVIEW_COMPLETED) {
    if (item.bugFixFromCpYn === Confirmation.YES) {
      return t('studio.execution_setting.select_build.build_category_fix_bug');
    }
    return t('studio.execution_setting.select_build.build_category_review_passed');
  } else if ((item.releaseStatus === RELEASE_STATUS_BUILD.COMING_SOON && item.releaseYn === Confirmation.YES)) {
    return t('studio.execution_setting.select_build.build_category_coming_soon');
  } else if (item.releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE) {
    return t('studio.execution_setting.select_build.build_category_live');
  }

  // Default case
  return `[${item.releaseStatus || ''}]`;
};

const formatBuildName = (buildNo: number, description?: string): string => {
  if (!buildNo) { return ''; }

  return description
    ? `${buildNo} - ${description}`
    : `${buildNo}`;
};

const fetchBuilds = async () => {
  // Get product number from router params
  const productId = router.currentRoute.value.params.productId as string;
  const productNo = parseInt(productId, 10);

  if (isNaN(productNo)) {
    return;
  }

  const response = await fetchListBuildReleaseAfterProductLiveRunOptionsApi(productNo) as ApiResponse | any[];

  if (response) {
    // Map the data to our required format
    const builds: BuildItem[] = [];

    // Handle response whether it's an array or has a contents property
    const items = Array.isArray(response)
      ? response
      : (response.contents && Array.isArray(response.contents)) ? response.contents : [];

    // Process each item
    items.forEach((item: BuildLaunchSettings) => {
      builds.push({
        id: item.buildId,
        buildNo: item.buildNo || 0,
        description: item.buildDescription,
        releaseStatus: item.releaseStatus || '',
        mode: getReleaseStatusText(item),
        name: formatBuildName(item.buildNo || 0, item.buildDescription),
        gameId: item.gameId || '',
        releaseYn: item.releaseYn
      });
    });

    valueList.value = builds;
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
  if (props.onClose) { props.onClose(); }
};

const handleDropdownChange = () => {
  if (selectedValue.value) {
    buildSelectionError.value = '';

    // Check if there are any builds with COMING_SOON status
    hasComingSoonBuildWarning.value = checkForComingSoonBuilds();

    // If build has releaseStatus is APPLY_LIVE then default choose Immediate and disable Scheduled
    if (selectedValue.value.releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE) {
      liveDate.value = Confirmation.YES; // choose Immediate
    }
  }
};

// Validate build selection
const validateBuildSelection = () => {
  if (!selectedValue.value && !isWebPlatform.value) {
    buildSelectionError.value = generateErrorMsg('studio.execution_setting.live_apply_popup_build_required_msg', RuleNames.REQUIRED);
    return false;
  }
  buildSelectionError.value = '';
  return true;
};

// Handle PC platform
const handlePcPlatform = async (): Promise<ApplyLiveReleaseBuildParams | false> => {
  // 1. Validate build selection
  if (!validateBuildSelection()) {
    return false;
  }

  // 2. Validate datetime if scheduled
  if (liveDate.value === Confirmation.NO && dateTimeError.value) {
    return false;
  }

  // 3. Check PC SDK - case game basic or game demo
  if (currentProductDetailType.value === PRODUCT_TYPE_DETAIL.GAME_BASIC ||
    currentProductDetailType.value === PRODUCT_TYPE_DETAIL.GAME_DEMO) {
    if (selectedValue.value?.id) {
      const buildIdStr = String(selectedValue.value.id);
      const buildMetaInfo = await getBuildMetaReleaseInfoApi(buildIdStr);

      if (buildMetaInfo) {
        if (!buildMetaInfo.runOptions || buildMetaInfo.runOptions.length === 0) {
          await showAlert({
            severity: 'info',
            content: t('studio.execution_setting.data_empty.cannot_apply_live_msg'),
            confirmLabel: t('studio.common.popup_case_cf_btn'),
            confirmClasses: '!max-w-full !w-full'
          });
          return false;
        }

        if (!buildMetaInfo.isSdkApplied && buildMetaInfo.runOptions) {
          const invalidRunOption = buildMetaInfo.runOptions.find((option: any) => !option.exist);

          if (invalidRunOption) {
            const result = await showConfirm({
              severity: 'info',
              content: t('studio.post_release_exe_opt_update.invalid_exe_file_msg'),
              confirmLabel: t('studio.build_mgmt_pg_redirect_btn'),
              cancelLabel: t('studio.common.popup_case_close_btn'),
              cancelVariant: 'outline'
            });

            if (result) {
              redirectTo(PRODUCT_BUILD_PAGE_URL, {
                open: { target: '_blank' }
              });
            }
            return false;
          }
        }
      }
    }
  } else {
    const installPathAndPcClientSettings = await fetchInstallPathAndPcClientSettingsOnTabApi(selectedValue.value?.gameId || '', RUN_OPTION_STATUS.TEST);
    if (!installPathAndPcClientSettings) {
      await showAlert({
        severity: 'info',
        content: t('studio.execution_setting.data_empty.cannot_apply_live_msg'),
        confirmLabel: t('studio.common.popup_case_cf_btn'),
        confirmClasses: '!max-w-full !w-full'
      });
      return false;
    }
  }

  // 4. Show confirm dialog
  const confirmed = await showDialog({
    component: ConfirmReleaseBuild,
    props: {
      class: 'max-w-[31.2rem]'
    },
    severity: 'info'
  });

  if (!confirmed) {
    return false;
  }

  // 5. Create and return params
  const buildId = String(selectedValue.value!.id);
  const gameId = selectedValue.value!.gameId;

  return {
    gameId,
    buildId,
    releaseType: liveDate.value === Confirmation.YES
      ? APPLY_LIVE_RELEASE_TYPE.IMMEDIATE
      : APPLY_LIVE_RELEASE_TYPE.SCHEDULED,
    ...(liveDate.value === Confirmation.NO &&
      selectedDate.value && { scheduledStartAt: new Date(selectedDate.value).getTime() })
  };
};

const handleApply = handleSubmit(async () => {
  const pcParams = await handlePcPlatform();
  if (!pcParams) {
    return;
  }

  await applyPcLiveApi(pcParams);
  handleClose();
});

const applyPcLiveApi = async (params: ApplyLiveReleaseBuildParams) => {
  const result = await createApplyLiveReleaseBuildApi(params);

  if (result?.code === API_SUCCESS_STATUS_CODE) {
    await showAlert({
      severity: 'info',
      content: t('studio.common.popup_case_iv_release_guide'),
      confirmLabel: t('studio.common.popup_case_cf_btn'),
      confirmClasses: '!max-w-full !w-full'
    });

    // Determine target tab based on release type
    const targetTab = params.releaseType === APPLY_LIVE_RELEASE_TYPE.IMMEDIATE
      ? RUN_OPTION_STATUS.LIVE
      : RUN_OPTION_STATUS.COMING_SOON;

    // Open launch settings page in a new tab with appropriate tab parameter
    redirectTo(PRODUCT_LAUNCH_SETTING_PAGE_URL + '?tab=' + targetTab, {
      open: { target: '_self' }
    });
  } else {
    await showAlert({
      severity: 'info',
      content: t(COMMON_ERROR_MESSAGE_KEY),
      confirmLabel: t('studio.common.popup_case_cf_btn'),
      confirmClasses: '!max-w-full !w-full'
    });
  }
};

const handleItemClick = (item: BuildItem) => {
  selectedValue.value = item;
  handleDropdownChange();
};

const isItemDisabled = (item: BuildItem): boolean => {
  return item.releaseStatus === RELEASE_STATUS_BUILD.VERIFY_UNDER_REVIEW &&
         (!item.revisionTypes || item.revisionTypes !== RevisionType.BugFix);
};

// Function to determine if the live date selection section should be shown
const shouldShowLiveDateSelection = () => {
  if (!selectedValue.value) {
    const verifyCondition = props.verifyStatus === VERIFY_STATUS.PASS && props.planStatus !== PLAN_STATUS.RELEASE;
    return !verifyCondition;
  }

  // Check build status using releaseStatus and releaseYn directly
  const { releaseStatus, releaseYn } = selectedValue.value;

  // Hide date selection for APPLY_LIVE and COMING_SOON with releaseYn=YES
  if (releaseStatus === RELEASE_STATUS_BUILD.APPLY_LIVE ||
      (releaseStatus === RELEASE_STATUS_BUILD.COMING_SOON && releaseYn === Confirmation.YES)) {
    // Set liveDate to Immediate when hiding the selection
    liveDate.value = Confirmation.YES;
    return false;
  }

  // Show date selection for all other cases
  const verifyCondition = props.verifyStatus === VERIFY_STATUS.PASS && props.planStatus !== PLAN_STATUS.RELEASE;
  return !verifyCondition;
};

onMounted(fetchBuilds);
</script>
